import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { ArrowUpRightIcon, SparklesIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button/Button';

export interface EnterpriseUpgradeCardProps {
  /**
   * Click handler for the primary "Talk to sales" CTA. Wired by the
   * page — typically `mailto:sales@tryhero.io` (or a contact-sales
   * dialog when that ships).
   */
  onContactSales: () => void;
}

/**
 * Dark-tier upgrade prompt rendered on `/billing` for orgs already on
 * the `growth` plan. Enterprise pricing isn't self-serve, so the CTA
 * is "Talk to sales" rather than the regular `/billing/plan` picker.
 *
 * Visual language mirrors the `premium` tier of `<PlanCard />`
 * (dark slate gradient + sparkle accent + outlined white CTA) so the
 * two surfaces feel like the same product family. The rounded
 * `bg-white/10` icon chip is the dark analog of the primary-tinted
 * `bg-primary/10` glyph on `<CurrentPlanCard />`.
 *
 * Don't render this on `/billing` while there's an open (pending /
 * partially_paid / past_due) Billing — the page must finish settling
 * one thing before promoting the next.
 */
export function EnterpriseUpgradeCard({
  onContactSales,
}: EnterpriseUpgradeCardProps) {
  const { t } = useTranslation('m-billing');

  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-2xl border border-gray-800 p-5 text-white shadow-sm',
        'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-800',
      )}>
      {/* Soft sparkle wash in the background — purely decorative, the
          content rendered above remains a normal column flow. The
          gradient sits behind everything via `-z-10` so any
          interactive element keeps full pointer-events. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.25),transparent_55%)]"
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-xl',
              'bg-white/10 text-white ring-1 ring-inset ring-white/15',
            )}>
            <SparklesIcon className="size-5" />
          </div>
          <div className="flex flex-col gap-0.5">
            <h3 className="text-base font-semibold text-white">
              {t('subscription.enterprise_upgrade.title')}
            </h3>
            <p className="text-sm text-gray-300">
              {t('subscription.enterprise_upgrade.description')}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={onContactSales}
          className={cn(
            'shrink-0 border-white/30 bg-white text-gray-900',
            'hover:bg-gray-100 hover:text-gray-900',
          )}>
          {t('subscription.enterprise_upgrade.cta')}
          {/*
            ArrowUpRight reads as "outward / upgrade" in LTR; we mirror
            (not rotate) for RTL to preserve the "out and up" meaning —
            same rationale as the `Upgrade plan` button on
            `<CurrentPlanCard />`.
          */}
          <ArrowUpRightIcon className="rtl:-scale-x-100" />
        </Button>
      </div>
    </section>
  );
}
